import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Heart, Sparkles, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { THERAPPI_AI_BRAND } from '@/lib/utils';

export interface TherappiSupportCardProps {
  journeyType: 'pregnancy' | 'postpartum' | 'perimenopause' | 'menopause' | 'menstrual' | 'ivf';
}

const JOURNEY_CONTENT = {
  pregnancy: {
    title: "Pregnancy Support",
    emoji: "🤰",
    stat: "Research shows that therapeutic support during pregnancy can reduce the risk of postnatal depression by up to 40%",
    message: `Pregnancy can be a beautiful yet overwhelming journey filled with physical changes, emotional shifts, and countless questions. If you're struggling right now or need additional guidance, ${THERAPPI_AI_BRAND} offers 24/7 compassionate support tailored specifically for pregnancy-related challenges.`,
    benefits: [
      "Process anxiety about birth and motherhood",
      "Navigate relationship changes",
      "Manage pregnancy-related stress and mood changes",
      "Develop coping strategies for the journey ahead"
    ]
  },
  postpartum: {
    title: "Postpartum Support",
    emoji: "👶",
    stat: "Studies indicate that 1 in 7 women experience postpartum depression, but early therapeutic support significantly improves outcomes",
    message: `The postpartum period can be incredibly challenging with sleep deprivation, hormonal changes, identity shifts, and overwhelming responsibility. You don't have to navigate this alone. ${THERAPPI_AI_BRAND} provides judgment-free support whenever you need it.`,
    benefits: [
      "Process the emotional rollercoaster of new motherhood",
      "Work through birth trauma or disappointment",
      "Manage anxiety about your baby's wellbeing",
      "Find yourself again while caring for your newborn"
    ]
  },
  perimenopause: {
    title: "Perimenopause Support",
    emoji: "🌸",
    stat: "Over 80% of women experience symptoms during perimenopause, yet many struggle to get their concerns validated or addressed",
    message: `Perimenopause can feel confusing and isolating. Irregular periods, mood swings, sleep issues, and cognitive changes can make you question what's happening to your body. ${THERAPPI_AI_BRAND} helps you understand your experience and develop strategies to thrive through this transition.`,
    benefits: [
      "Understand which symptoms are hormone-related",
      "Develop strategies to manage mood and anxiety",
      "Navigate the emotional aspects of life transitions",
      "Get support for the changes you're experiencing"
    ]
  },
  menopause: {
    title: "Menopause Support",
    emoji: "🦋",
    stat: "Women who receive support during menopause report 65% better quality of life and emotional wellbeing",
    message: `Menopause is a significant life transition that goes beyond hot flashes. The emotional, psychological, and identity shifts deserve attention and support. ${THERAPPI_AI_BRAND} understands the complexity of this journey and provides personalised guidance.`,
    benefits: [
      "Process identity changes and life transitions",
      "Manage anxiety, depression, and mood changes",
      "Develop resilience through this transformation",
      "Find empowerment in your post-reproductive years"
    ]
  },
  menstrual: {
    title: "Cycle Support",
    emoji: "🌙",
    stat: "Research shows that 90% of menstruating individuals experience PMS symptoms, with therapeutic support improving symptom management by 50%",
    message: `Your menstrual cycle affects more than just your body-it impacts your mood, energy, relationships, and daily life. If you're struggling with cycle-related challenges, ${THERAPPI_AI_BRAND} offers specialised support for hormonal mental health.`,
    benefits: [
      "Understand the emotional impact of your cycle",
      "Develop strategies for managing PMDD or severe PMS",
      "Work through cycle-related anxiety or depression",
      "Create lifestyle approaches that honor your cycle"
    ]
  },
  ivf: {
    title: "IVF Journey Support",
    emoji: "💝",
    stat: "The IVF journey can be one of the most emotionally and physically demanding experiences, with studies showing significant benefits from psychological support",
    message: `The IVF process involves hope, disappointment, waiting, medical procedures, and intense emotional swings. Whether you're just starting or have been on this journey for a while, ${THERAPPI_AI_BRAND} provides specialised support for fertility challenges.`,
    benefits: [
      "Process the grief and hope of fertility treatment",
      "Manage anxiety during the two-week wait",
      "Navigate relationship strain and communication",
      "Develop resilience through treatment cycles"
    ]
  }
};

export function TherappiSupportCard({ journeyType }: TherappiSupportCardProps) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const content = JOURNEY_CONTENT[journeyType];

  const handleGetSupport = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast({
          title: "Sign in required",
          description: "Please sign in to access Therappi AI support",
          variant: "destructive"
        });
        return;
      }

      // Call edge function to award journey bonus credits
      const { data, error } = await supabase.functions.invoke('manage-credits', {
        body: {
          action: 'award_journey_bonus',
          journey_type: journeyType,
          bonus_amount: 10
        }
      });

      if (!error && data?.awarded) {
        toast({
          title: "Welcome bonus! 🎁",
          description: "You've received 10 free Therappi AI credits to get started",
        });
      }

      navigate('/therapy', { state: { from: window.location.pathname } });
    } catch (error) {
      console.error('Error handling support access:', error);
      navigate('/therapy', { state: { from: window.location.pathname } });
    }
  };

  return (
    <Card className="p-6 bg-gradient-to-br from-primary/5 via-secondary/5 to-primary/10 border-primary/20">
      <div className="space-y-4">
        <div className="flex items-start gap-3">
          <div className="text-4xl">{content.emoji}</div>
          <div className="flex-1">
            <h3 className="font-semibold text-lg mb-2 flex items-center gap-2">
              <Heart className="h-5 w-5 text-primary" />
              {content.title}
            </h3>
          </div>
        </div>

        <div className="bg-background/50 p-4 rounded-lg border border-primary/10">
          <p className="text-sm font-medium text-primary mb-2">📊 Research Insight</p>
          <p className="text-sm">{content.stat}</p>
        </div>

        <p className="text-sm leading-relaxed">{content.message}</p>

        <div className="space-y-2">
          <p className="text-sm font-semibold">How Therappi AI can help:</p>
          <ul className="space-y-1">
            {content.benefits.map((benefit, index) => (
              <li key={index} className="text-sm flex items-start gap-2">
                <Sparkles className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                <span>{benefit}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="bg-primary/5 p-4 rounded-lg border border-primary/20">
          <p className="text-sm font-semibold mb-2">💝 Special Offer for You</p>
          <p className="text-sm mb-3">
            Try {THERAPPI_AI_BRAND} with <strong>10 free credits</strong> to experience personalised support. 
            If you find it helpful, continue with flexible monthly access that you can cancel anytime-no pressure, just support when you need it.
          </p>
          <Button 
            onClick={handleGetSupport}
            className="w-full group"
          >
            Get Started with {THERAPPI_AI_BRAND}
            <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
          </Button>
        </div>

        <p className="text-xs text-muted-foreground text-center">
          Available 24/7 • Evidence-based approaches • Completely confidential
        </p>
      </div>
    </Card>
  );
}